import { useState, useEffect, useMemo } from 'react';

import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { formatIncompletePhoneNumber } from 'libphonenumber-js';
import moment from 'moment';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { fetchConsentFormInformation, downloadConsentFormPdf } from 'modules/state-mgmt/workerData';
import { isMobileDevice, prettifyErrorResponse } from 'utils';
import { YES_NO_OPTIONS, SUPERVISOR_OPTIONS, SKILLED_NON_SKILLED_OPTIONS } from 'utils/optionsUtil';

import { actionMapForOtherLoaders } from 'constants/actions';
import { ConsentAgreementKind, GOV_ID_TYPE_OTHER_OPTION_ID } from 'constants/common';
import { genderMap } from 'models/general';

import { WORKER_PROFILE_FIELD_CODE, expandEmailOrMobilePhoneField } from 'modules/views/pages/ConsentForm/constants/workerFieldsMap';

import { IAcceptedConsentAgreement, IConsentFormAccordion } from '../types';

export enum ConsentFormSections {
  PERSONAL_INFORMATION = 'personalInformation',
  CONTACT_INFORMATION = 'contactInformation',
  HOME_ADDRESS = 'homeAddress',
  EMERGENCY_CONTACT = 'emergencyContact',
  GOV_ISSUED_ID = 'govIssuedId',
  WORKER_TRADES = 'workerTrades',
  PROJECT_INFORMATION = 'projectInformation',
  SIGNATURE = 'signature',
}

/**
 * Accordion keys for the signed consent forms are derived from the agreement
 * content id, prefixed so they can never collide with a `ConsentFormSections`
 * key inside the shared `openedAccordions` map.
 */
export const getConsentFormAccordionKey = (consentAgreementContentId: string) =>
  `consentForm:${consentAgreementContentId}`;

type UseConsentFormInformation = () => {
  isLoadingInformation: boolean;
  isDownloadLoading: boolean;
  data: Record<
    string,
    {
      title: string;
      fields?: { label: string; value: string; hasSectionSeparator?: boolean }[];
      sections?: Array<{
        title: string;
        fields: Array<{
          label: string;
          value: string;
          hasSectionSeparator?: boolean;
          fullWidth?: boolean;
          imageUrls?: string[];
        }>;
        hasSectionSeparator?: boolean;
      }>;
      responsiveValues?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
      };
      data?: string;
      imageUrl?: string;
      date?: string;
      timeZone?: string;
    }
  >;
  consentForms: IConsentFormAccordion[];
  openedAccordions: Record<string, boolean>;
  setOpenedAccordions: any;
  downloadConsentForm: () => void;
  getConsentFormInformation: () => void;
  assignedCompanies: {
    id: string;
    name: string;
    workerProjectStatus: number;
    workerProjectId?: string | null;
  }[];
  selectedEmployer: {
    id: string;
    name: string;
    workerProjectStatus: number;
    workerProjectId?: string | null;
  } | null;
  isWorker: boolean;
  setSelectedEmployer: (
    employer: { id: string; name: string; workerProjectStatus: number; workerProjectId?: string | null } | null,
  ) => void;
};

const STORED_FIELD_BOOL_OPTIONS: Record<string, { en: { id: any; name: string }[]; es: { id: any; name: string }[] }> =
  {
    [WORKER_PROFILE_FIELD_CODE.Role]: SUPERVISOR_OPTIONS,
    [WORKER_PROFILE_FIELD_CODE.WorkExperience]: SKILLED_NON_SKILLED_OPTIONS,
    [WORKER_PROFILE_FIELD_CODE.VeteranStatus]: YES_NO_OPTIONS,
    [WORKER_PROFILE_FIELD_CODE.LaborUnionStatus]: YES_NO_OPTIONS,
  };

const IMAGE_CODES = new Set([WORKER_PROFILE_FIELD_CODE.GovernmentIDImage, WORKER_PROFILE_FIELD_CODE.ProfilePhoto]);

const parseImageUrls = (value: string): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
  } catch {}
  return value
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
};

const formatStoredFieldValue = (code: string, value: string, languageCode?: string): string => {
  if (code === WORKER_PROFILE_FIELD_CODE.DateOfBirth) {
    return value ? moment(value).format('DD MMM YYYY') : value;
  }
  const lang = (languageCode || 'en').toLowerCase() as 'en' | 'es';
  const optionSet = STORED_FIELD_BOOL_OPTIONS[code];
  if (optionSet) {
    const options = optionSet[lang] ?? optionSet.en;
    return options.find(o => String(o.id).toLowerCase() === value?.toLowerCase())?.name ?? value;
  }
  return value;
};

const isSmallDevice = window.innerWidth <= 600 || isMobileDevice();
const getLabelWithColon = (label: string) => `${label}:`;
const getValueWithDash = (value: string) => (value ? value : '--');

const useConsentFormInformation: UseConsentFormInformation = () => {
  const [openedAccordions, setOpenedAccordions] = useState<Record<string, boolean>>({
    [ConsentFormSections.PERSONAL_INFORMATION]: true,
    [ConsentFormSections.CONTACT_INFORMATION]: isSmallDevice ? false : true,
    [ConsentFormSections.HOME_ADDRESS]: isSmallDevice ? false : true,
    [ConsentFormSections.EMERGENCY_CONTACT]: isSmallDevice ? false : true,
    [ConsentFormSections.GOV_ISSUED_ID]: isSmallDevice ? false : true,
    [ConsentFormSections.WORKER_TRADES]: isSmallDevice ? false : true,
    [ConsentFormSections.PROJECT_INFORMATION]: isSmallDevice ? false : true,
    [ConsentFormSections.SIGNATURE]: isSmallDevice ? false : true,
  });
  const [selectedEmployer, setSelectedEmployer] = useState(null);

  const {
    onboarding,
    consentFormInformation,
    projectId,
    projectName,
    languageCode,
    isLoadingInformation,
    isDownloadLoading,
    assignedCompanies,
    isWorker,
  } = useAppSelector(state => ({
    onboarding: state.onboarding || {},
    consentFormInformation: state.workerData?.consentFormInformation || {},
    projectId: state.cache?.project?.id || state.onboarding?.temp?.project?.value,
    projectName: state.cache?.project?.name || state.onboarding?.temp?.project?.label,
    languageCode: state.app?.languageCode,
    isLoadingInformation:
      state.loader?.otherLoaders?.[actionMapForOtherLoaders.fetchConsentFormInformation]?.isLoading || false,
    isDownloadLoading:
      state.loader?.otherLoaders?.[actionMapForOtherLoaders.downloadConsentFormPdf]?.isLoading || false,
    assignedCompanies: state.onboarding?.assignedCompanies || [],
    isWorker: state.user?.isWorker,
  }));

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const getConsentFormInformation = async () => {
    try {
      if (!selectedEmployer?.workerProjectId) {
        enqueueSnackbar(t('consentFormInformation.selectEmployerError'), { variant: 'error' });
        return;
      }
      await dispatch(
        fetchConsentFormInformation({
          workerId: onboarding.id,
          projectId: selectedEmployer?.workerProjectId,
          companyId: selectedEmployer?.value,
          languageCode: languageCode,
        }),
      ).unwrap();
    } catch (err) {
      enqueueSnackbar(prettifyErrorResponse(err), { variant: 'error' });
    }
  };

  const downloadConsentForm = async () => {
    try {
      if (!selectedEmployer?.workerProjectId) {
        enqueueSnackbar(t('consentFormInformation.selectEmployerError'), { variant: 'error' });
        return;
      }
      await dispatch(
        downloadConsentFormPdf({
          workerId: onboarding.id,
          projectId: selectedEmployer?.workerProjectId,
          companyId: selectedEmployer?.value,
          languageCode: languageCode,
          fileName: `Consent Form ${consentFormInformation?.createdAtTimeZone?.value ? moment(consentFormInformation?.createdAtTimeZone?.value).format('YYYY-MM-DD') : ''} ${projectName}-${consentFormInformation.firstName} ${consentFormInformation.lastName}`,
        }),
      ).unwrap();
    } catch (err) {
      enqueueSnackbar(prettifyErrorResponse(err), { variant: 'error' });
    }
  };

  // The consent forms the worker signed for this project (`acceptedAgreements.items`
  // on the consent form GET — FN-5283), ordered as the project defines them. The API
  // returns one row per form per language, so keep only the active language's copy.
  const consentForms: IConsentFormAccordion[] = useMemo(() => {
    const acceptedAgreements: IAcceptedConsentAgreement[] = consentFormInformation?.acceptedAgreements?.items || [];

    return acceptedAgreements
      .filter(agreement => agreement.languageCode === (languageCode || 'en'))
      .sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0))
      .map(agreement => ({
        key: getConsentFormAccordionKey(agreement.consentAgreementContentId),
        title: agreement.name,
        htmlContent: agreement.htmlContent,
        chipLabel:
          agreement.kind === ConsentAgreementKind.Standard
            ? t('consentFormInformation.standardFormChip')
            : t('consentFormInformation.customFormChip'),
        chipVariant: agreement.kind === ConsentAgreementKind.Standard ? ('green' as const) : ('yellow' as const),
      }));
  }, [consentFormInformation?.acceptedAgreements, languageCode, t]);

  // Consent form accordion keys are dynamic, so seed each new one with the same
  // default-expanded behaviour as the static sections.
  useEffect(() => {
    setOpenedAccordions(prev => {
      const missingKeys = consentForms.filter(form => !(form.key in prev));
      if (missingKeys.length === 0) {
        return prev;
      }
      return {
        ...prev,
        ...Object.fromEntries(missingKeys.map(form => [form.key, !isSmallDevice])),
      };
    });
  }, [consentForms]);

  useEffect(() => {
    if (selectedEmployer) {
      getConsentFormInformation();
    }
  }, [selectedEmployer]);

  useEffect(() => {
    const filteredCompanies = assignedCompanies.filter(company => !!company?.workerProjectId);
    if (filteredCompanies?.length > 0 && !selectedEmployer) {
      const defaultCompany = {
        label: filteredCompanies[0].name,
        value: filteredCompanies[0].id,
        workerProjectId: filteredCompanies[0].workerProjectId,
      };
      setSelectedEmployer(defaultCompany);
    }
  }, [assignedCompanies, selectedEmployer]);

  const data = {
    [ConsentFormSections.PERSONAL_INFORMATION]: {
      title: t('personalInformation.personalInformation.title'),
      fields: [
        {
          label: getLabelWithColon(t('personalInformation.personalInformation.firstName')),
          value: getValueWithDash(consentFormInformation.firstName),
        },
        {
          label: getLabelWithColon(t('personalInformation.personalInformation.middleName')),
          value: getValueWithDash(consentFormInformation.middleName),
        },
        {
          label: getLabelWithColon(t('personalInformation.personalInformation.lastName')),
          value: getValueWithDash(consentFormInformation.lastName),
        },
        {
          label: getLabelWithColon(t('personalInformation.personalInformation.dateOfBirth')),
          value: getValueWithDash(
            consentFormInformation.dateOfBirth ? moment(consentFormInformation.dateOfBirth).format('DD MMM YYYY') : '',
          ),
        },
        {
          label: getLabelWithColon(t('personalInformation.personalInformation.gender')),
          value: getValueWithDash(genderMap[consentFormInformation.gender]),
        },
        {
          label: getLabelWithColon(t('personalInformation.personalInformation.ethnicity')),
          value: getValueWithDash(consentFormInformation.ethnicity?.name),
        },
        {
          label: getLabelWithColon(t('personalInformation.personalInformation.primaryLanguage')),
          value: getValueWithDash(consentFormInformation.primaryLanguage?.name),
        },
        {
          label: getLabelWithColon(t('personalInformation.personalInformation.veteran')),
          value: getValueWithDash(
            typeof consentFormInformation.isVeteran === 'boolean'
              ? consentFormInformation.isVeteran
                ? 'Yes'
                : 'No'
              : null,
          ),
        },
        {
          label: getLabelWithColon(t('personalInformation.personalInformation.allergies')),
          value: getValueWithDash(consentFormInformation.allergies),
        },
        {
          label: getLabelWithColon(t('personalInformation.personalInformation.lastSSN')),
          value: getValueWithDash(consentFormInformation.socialSecurityNumber),
        },
      ],
      responsiveValues: {
        xs: 6,
        sm: 6,
        md: 6,
        lg: 4,
        xl: 4,
      },
    },
    [ConsentFormSections.CONTACT_INFORMATION]: {
      title: t('contactInformation.title'),
      fields: [
        {
          label: getLabelWithColon(t('contactInformation.contactInfo.email')),
          value: getValueWithDash(consentFormInformation.email),
        },
        {
          label: getLabelWithColon(t('contactInformation.contactInfo.mobileNumber')),
          value: getValueWithDash(
            consentFormInformation.mobilePhoneNumber
              ? formatIncompletePhoneNumber(consentFormInformation.mobilePhoneNumber)
              : null,
          ),
        },
        {
          label: getLabelWithColon(t('contactInformation.contactInfo.alternatePhone')),
          value: getValueWithDash(consentFormInformation.phoneNumber),
        },
      ],
    },
    [ConsentFormSections.HOME_ADDRESS]: {
      title: t('contactInformation.homeAddress.title'),
      fields: [
        {
          label: getLabelWithColon(t('contactInformation.homeAddress.addressLine1')),
          value: getValueWithDash(consentFormInformation.address?.line1),
        },
        {
          label: getLabelWithColon(t('contactInformation.homeAddress.addressLine2')),
          value: getValueWithDash(consentFormInformation.address?.line2),
        },
        {
          label: getLabelWithColon(t('contactInformation.homeAddress.country')),
          value: getValueWithDash(consentFormInformation.address?.country?.name),
        },
        {
          label: getLabelWithColon(t('contactInformation.homeAddress.state')),
          value: getValueWithDash(consentFormInformation.address?.stateName),
        },
        {
          label: getLabelWithColon(t('contactInformation.homeAddress.county')),
          value: getValueWithDash(consentFormInformation.address?.county),
        },
        {
          label: getLabelWithColon(t('contactInformation.homeAddress.city')),
          value: getValueWithDash(consentFormInformation.address?.city),
        },
        {
          label: getLabelWithColon(t('contactInformation.homeAddress.zipCode')),
          value: getValueWithDash(consentFormInformation.address?.zipCode),
        },
        ...(consentFormInformation.address?.borough
          ? [
              {
                label: getLabelWithColon(t('contactInformation.homeAddress.borough')),
                value: getValueWithDash(consentFormInformation.address?.borough),
              },
            ]
          : []),
      ],
    },
    [ConsentFormSections.EMERGENCY_CONTACT]: {
      title: t('contactInformation.emergencyContact.title'),
      fields: [
        {
          label: getLabelWithColon(t('consentFormInformation.emergencyContactName')),
          value: getValueWithDash(consentFormInformation.emergencyContactName),
        },
        {
          label: getLabelWithColon(t('consentFormInformation.emergencyContactPhone')),
          value: getValueWithDash(consentFormInformation.emergencyContactPhone),
        },
        {
          label: getLabelWithColon(t('contactInformation.emergencyContact.relationship')),
          value: getValueWithDash(consentFormInformation.emergencyContactRelationship),
        },
      ],
    },
    [ConsentFormSections.GOV_ISSUED_ID]: {
      title: t('personalInformation.govtId.title'),
      fields: [
        {
          label: getLabelWithColon(t('personalInformation.govtId.idType')),
          value: getValueWithDash(consentFormInformation.identificationType?.name),
        },
        {
          label: getLabelWithColon(t('personalInformation.govtId.idNumber')),
          value: getValueWithDash(consentFormInformation.identificationNumber),
        },
        {
          label: getLabelWithColon(t('personalInformation.govtId.issuedBy')),
          value: getValueWithDash(
            consentFormInformation.identificationType?.id === GOV_ID_TYPE_OTHER_OPTION_ID
              ? consentFormInformation.otherIdentificationType
              : consentFormInformation.identificationGeographicLocation?.name,
          ),
        },
      ],
    },
    [ConsentFormSections.WORKER_TRADES]: {
      title: t('consentFormInformation.workerTrades'),
      fields: [
        {
          label: getLabelWithColon(t('trades.role')),
          value: getValueWithDash(
            typeof consentFormInformation.isSupervisor === 'boolean'
              ? consentFormInformation.isSupervisor
                ? 'Supervisor'
                : 'Non Supervisor'
              : null,
          ),
        },
        {
          label: getLabelWithColon(t('trades.workExperience')),
          value: getValueWithDash(
            typeof consentFormInformation.isSkilled === 'boolean'
              ? consentFormInformation.isSkilled
                ? 'Skilled'
                : 'Non Skilled'
              : null,
          ),
        },
        {
          label: getLabelWithColon(t('trades.laborUnion')),
          value: getValueWithDash(
            typeof consentFormInformation.isAffiliatedToLaborUnion === 'boolean'
              ? consentFormInformation.isAffiliatedToLaborUnion
                ? 'Yes'
                : 'No'
              : null,
          ),
        },
        {
          label: getLabelWithColon(t('trades.laborUnionNumber')),
          value: getValueWithDash(consentFormInformation.laborUnionNumber),
        },
        {
          label: getLabelWithColon(t('trades.title')),
          value: getValueWithDash(
            consentFormInformation.trades?.length > 0
              ? consentFormInformation.trades?.map(trade => trade.name).join(', ')
              : null,
          ),
        },
      ],
    },
    [ConsentFormSections.PROJECT_INFORMATION]: (() => {
      const allConsentFields = consentFormInformation?.consentFormFields || [];
      const standardFields = allConsentFields
        .filter(f => f.consentFormFieldType === 'Standard')
        .map(f => ({ label: f.displayName, value: getValueWithDash(f.displayValue) }));
      const customFields = allConsentFields
        .filter(f => f.consentFormFieldType === 'Custom')
        .map(f => ({ label: f.displayName, value: getValueWithDash(f.displayValue) }));
      const STORED_FIELDS_LAST = new Set(['WorkerGovernmentIDImage', 'WorkerProfilePhoto']);
      const allStoredRaw = consentFormInformation?.storedFields || [];
      const resolvedStoredFields = expandEmailOrMobilePhoneField(
        allStoredRaw.filter(f => f.isStored && f.isVisible),
        consentFormInformation?.email,
        consentFormInformation?.mobilePhoneNumber,
        allStoredRaw,
      );

      const storedFields = resolvedStoredFields
        .sort((a, b) => {
          const aLast = STORED_FIELDS_LAST.has(a.code) ? 1 : 0;
          const bLast = STORED_FIELDS_LAST.has(b.code) ? 1 : 0;
          return aLast - bLast;
        })
        .map(f => ({
          label: f.name,
          value: IMAGE_CODES.has(f.code as any)
            ? ''
            : getValueWithDash(formatStoredFieldValue(f.code, f.value, languageCode)),
          ...(IMAGE_CODES.has(f.code as any) ? { imageUrls: parseImageUrls(f.value) } : {}),
          ...(STORED_FIELDS_LAST.has(f.code) ? { fullWidth: true } : {}),
        }));
      const exemptedEduFields = Object.entries(consentFormInformation?.exemptedEducations || {}).map(
        ([label, value]) => ({ label, value: getValueWithDash(value as any) }),
      );

      const sections: Array<{
        title: string;
        fields: Array<{ label: string; value: string }>;
        hasSectionSeparator?: boolean;
      }> = [
        {
          title: t('consentFormInformation.standardFields'),
          fields: standardFields,
          hasSectionSeparator: true,
        },
      ];

      if (customFields.length > 0) {
        sections.push({
          title: t('consentFormInformation.customFields'),
          fields: customFields,
          hasSectionSeparator: true,
        });
      }

      if (storedFields.length > 0) {
        sections.push({
          title: t('consentFormInformation.storedFields'),
          fields: storedFields,
          hasSectionSeparator: exemptedEduFields.length > 0,
        });
      }

      if (exemptedEduFields.length > 0) {
        sections.push({ title: '', fields: exemptedEduFields });
      }

      return { title: t('consentFormInformation.projectInformation'), sections };
    })(),
    [ConsentFormSections.SIGNATURE]: {
      title: t('signature.title'),
      imageUrl: consentFormInformation.signatureUrl,
      date: getValueWithDash(
        consentFormInformation?.createdAtTimeZone
          ? `${moment(consentFormInformation?.createdAtTimeZone?.value).format('MMM DD, YYYY - hh:mm a')} ${consentFormInformation?.createdAtTimeZone?.abbreviation}`
          : null,
      ),
    },
  };

  return {
    isLoadingInformation,
    isDownloadLoading,
    data,
    consentForms,
    openedAccordions,
    setOpenedAccordions,
    downloadConsentForm,
    getConsentFormInformation,
    assignedCompanies: assignedCompanies?.filter(company => !!company?.workerProjectId) || [],
    setSelectedEmployer,
    selectedEmployer,
    isWorker,
  };
};

export default useConsentFormInformation;
